import { Capacitor, registerPlugin } from '@capacitor/core';

const NativeRecordingGuardPlugin = registerPlugin('RecordingGuard');

function finiteNonNegative(value) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
}

export function normalizeNativeCapacity(value = {}) {
  return {
    availableBytes: finiteNonNegative(value.availableBytes),
    thermalSupported: value.thermalSupported === true,
    thermalStatus: finiteNonNegative(value.thermalStatus),
    thermalHeadroom: finiteNonNegative(value.thermalHeadroom)
  };
}

export function createNativeRecordingGuard({
  isNative = Capacitor.isNativePlatform(),
  plugin = NativeRecordingGuardPlugin
} = {}) {
  let active = false;

  const unsupported = () => ({
    supported: false,
    active: false,
    reason: 'web'
  });

  return {
    isActive: () => active,

    async start(recordingId = null) {
      if (!isNative) return unsupported();
      if (active) return { supported: true, active: true, idempotent: true };
      try {
        const result = await plugin.start({ recordingId });
        active = result?.active === true;
        return { supported: true, ...result, active };
      } catch (error) {
        active = false;
        return {
          supported: true,
          active: false,
          error: error?.message || String(error)
        };
      }
    },

    async stop() {
      if (!isNative) return unsupported();
      if (!active) return { supported: true, active: false, idempotent: true };
      try {
        const result = await plugin.stop();
        active = false;
        return { supported: true, ...result, active: false };
      } catch (error) {
        return {
          supported: true,
          active,
          error: error?.message || String(error)
        };
      }
    },

    async getCapacity() {
      if (!isNative) {
        return {
          ...unsupported(),
          ...normalizeNativeCapacity()
        };
      }
      try {
        return {
          supported: true,
          ...(normalizeNativeCapacity(await plugin.getCapacity()))
        };
      } catch (error) {
        return {
          supported: true,
          ...normalizeNativeCapacity(),
          error: error?.message || String(error)
        };
      }
    }
  };
}

export const nativeRecordingGuard = createNativeRecordingGuard();
